/**
 * htmlSanitizer.ts — Saneamento autoritativo de HTML importado no Studio
 *
 * Fonte de verdade ÚNICA: este arquivo mora nas Edge Functions (Deno) e é
 * reexportado para o navegador em `src/lib/studio/import`. Não existe cópia
 * espelhada. Ele não depende de DOM nem de bibliotecas externas, justamente
 * para rodar igual nos dois lados.
 *
 * O que ele faz:
 *   - remove elementos perigosos por completo (script, iframe, object, etc.);
 *   - remove atributos de evento (onclick...) e URLs `javascript:`;
 *   - mantém apenas tags e atributos da lista permitida;
 *   - preserva `data-uaizy-block` (System Slots) e `data-uaizy-*` de leiaute;
 *   - aceita imagens `data:` apenas nos formatos de imagem previstos;
 *   - detecta variáveis `{{ caminho }}` usadas no documento;
 *   - devolve um relatório do que foi removido, para mostrar ao usuário.
 *
 * IMPORTANTE: no navegador este saneamento serve só para a experiência de uso.
 * A versão que vale é a executada no servidor (Edge Function studio-import-html).
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-11 v1.3.0 - Nova opção `permitirFormulario`: quando o HTML
 *            vem do compilador do próprio Studio (páginas públicas em blocos), o
 *            bloco `page.form` precisa manter form/input/select/label/button.
 *            O HTML importado de fora continua sem nenhum desses elementos.
 *   RELEASE: 2026-09-11 v1.2.0 - `scopeStudioCss` exportado: o fatiamento em
 *            seções (`htmlSections.ts`) escopa o CSS pelo mesmo caminho, sem
 *            criar uma segunda implementação.
 *   RELEASE: 2026-09-10 v1.1.0 - Isolamento do conteúdo importado: CSS escopado
 *            na raiz `.studio-raw-html-root`, remoção de `@import` e reescrita
 *            de regras globais (`html`, `body`, `*`).
 *   RELEASE: 2026-09-10 v1.0.0 - Versão inicial (Studio, Fase 4).
 */

/** Tags mantidas no HTML importado. */
export const STUDIO_ALLOWED_TAGS = new Set<string>([
  'html', 'head', 'body', 'meta', 'title', 'style',
  'div', 'span', 'p', 'br', 'hr', 'section', 'article', 'header', 'footer', 'main', 'aside', 'nav',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'small', 'sub', 'sup', 'mark', 'blockquote', 'pre', 'code',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'a', 'img', 'figure', 'figcaption', 'picture', 'source',
  // Sanfona nativa: usada pelos blocos interativos oficiais; abre e fecha sem
  // script e não executa nada.
  'details', 'summary',
]);

/** Elementos removidos junto com todo o conteúdo interno. */
export const STUDIO_FORBIDDEN_ELEMENTS = new Set<string>([
  'script', 'iframe', 'object', 'embed', 'applet', 'form', 'input', 'button', 'select',
  'textarea', 'option', 'label', 'link', 'base', 'noscript', 'svg', 'math', 'template', 'frame',
  'frameset', 'audio', 'video', 'canvas', 'dialog', 'portal',
]);

/** Atributos permitidos (além de `data-uaizy-*`). */
export const STUDIO_ALLOWED_ATTRS = new Set<string>([
  'href', 'src', 'alt', 'title', 'class', 'style', 'id', 'target', 'rel',
  'width', 'height', 'align', 'colspan', 'rowspan', 'charset', 'name', 'content', 'srcset', 'type',
  // Atributos de apresentação usados pelos blocos interativos (sem execução).
  'open', 'hidden', 'loading', 'role', 'aria-label', 'aria-hidden',
]);

/** Elementos de formulário liberados apenas para o HTML do próprio compilador. */
const STUDIO_FORM_TAGS = new Set<string>([
  'form', 'input', 'button', 'select', 'option', 'textarea', 'label',
]);

/** Atributos liberados apenas quando os elementos de formulário são aceitos. */
const STUDIO_FORM_ATTRS = new Set<string>([
  'action', 'method', 'placeholder', 'required', 'value', 'for', 'maxlength',
  'minlength', 'pattern', 'inputmode', 'autocomplete', 'rows', 'cols', 'step',
  'min', 'max', 'tabindex', 'selected', 'checked', 'disabled', 'readonly',
  'aria-hidden', 'aria-label',
]);

/** Opções do saneamento. */
export interface StudioSanitizeOptions {
  /**
   * Mantém os elementos de formulário. Use SOMENTE com HTML gerado pelo
   * compilador do Studio; nunca com conteúdo enviado por terceiros.
   */
  permitirFormulario?: boolean;
}

/** Formatos de imagem aceitos em `data:`. */
const DATA_IMAGE_RE = /^data:image\/(png|jpe?g|gif|webp|avif);base64,[a-z0-9+/=\s]+$/i;

/** Relatório devolvido pelo saneamento. */
export interface StudioSanitizeReport {
  /** Tags removidas por completo (nome → quantidade). */
  removedElements: Record<string, number>;
  /** Atributos removidos (nome → quantidade). */
  removedAttributes: Record<string, number>;
  /** Chaves de System Slot encontradas (`data-uaizy-block`). */
  slots: string[];
  /** Variáveis `{{ caminho }}` encontradas. */
  variables: string[];
  /** Imagens externas referenciadas por URL. */
  externalImages: string[];
  /** Quantidade de imagens embutidas em `data:` (viram arquivos no servidor). */
  inlineImages: number;
}

export interface StudioSanitizeResult {
  html: string;
  report: StudioSanitizeReport;
}

function conta(mapa: Record<string, number>, chave: string): void {
  mapa[chave] = (mapa[chave] ?? 0) + 1;
}

/** Decodifica entidades simples usadas para burlar filtros (&#106;...). */
function normalizaUrl(valor: string): string {
  return valor
    .replace(/&#x?([0-9a-f]+);?/gi, (_m, cod: string) => {
      const numero = /^x/i.test(_m.slice(1)) ? parseInt(cod, 16) : parseInt(cod, 10);
      return Number.isFinite(numero) ? String.fromCharCode(numero) : '';
    })
    .replace(/[\u0000-\u0020]/g, '')
    .toLowerCase();
}

/** Diz se a URL do atributo é aceitável. */
function urlPermitida(nomeAtributo: string, valor: string): boolean {
  const limpo = normalizaUrl(valor);
  if (limpo.startsWith('javascript:') || limpo.startsWith('vbscript:')) return false;
  if (limpo.startsWith('data:')) {
    // Somente imagens embutidas; qualquer outro `data:` é bloqueado.
    return nomeAtributo === 'src' && DATA_IMAGE_RE.test(valor.trim());
  }
  return true;
}

/** Remove declarações de estilo perigosas. */
function saneiaEstilo(valor: string): string {
  return valor
    .replace(/expression\s*\(/gi, '')
    .replace(/url\s*\(\s*["']?\s*(javascript|vbscript|data:text)/gi, 'url(');
}

/** Lê os atributos de uma tag e devolve a versão limpa. */
function saneiaAtributos(
  bruto: string,
  relatorio: StudioSanitizeReport,
  permiteFormulario = false,
): string {
  const partes: string[] = [];
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
  let m: RegExpExecArray | null;

  while ((m = re.exec(bruto)) !== null) {
    const nome = m[1].toLowerCase();
    const valor = m[2] ?? m[3] ?? m[4] ?? '';

    // Eventos JavaScript nunca passam
    if (nome.startsWith('on')) {
      conta(relatorio.removedAttributes, nome);
      continue;
    }

    const ehDadoUaizy = nome.startsWith('data-uaizy-');
    const ehAtributoFormulario = permiteFormulario && STUDIO_FORM_ATTRS.has(nome);
    if (!ehDadoUaizy && !ehAtributoFormulario && !STUDIO_ALLOWED_ATTRS.has(nome)) {
      conta(relatorio.removedAttributes, nome);
      continue;
    }

    if (
      (nome === 'href' || nome === 'src' || nome === 'srcset' || nome === 'action') &&
      !urlPermitida(nome, valor)
    ) {
      conta(relatorio.removedAttributes, nome);
      continue;
    }

    let valorFinal = valor;
    if (nome === 'style') valorFinal = saneiaEstilo(valor);

    if (nome === 'data-uaizy-block' && valorFinal) {
      if (!relatorio.slots.includes(valorFinal)) relatorio.slots.push(valorFinal);
    }

    if (nome === 'src' && valorFinal) {
      if (/^data:image\//i.test(valorFinal.trim())) relatorio.inlineImages += 1;
      else if (/^https?:\/\//i.test(valorFinal.trim()) && !relatorio.externalImages.includes(valorFinal)) {
        relatorio.externalImages.push(valorFinal);
      }
    }

    partes.push(
      valorFinal === '' && m[0].indexOf('=') === -1
        ? nome
        : `${nome}="${valorFinal.replace(/"/g, '&quot;')}"`,
    );
  }

  return partes.length ? ' ' + partes.join(' ') : '';
}

/** Coleta as variáveis `{{ caminho }}` presentes no conteúdo. */
export function detectStudioVariables(html: string): string[] {
  const encontradas: string[] = [];
  const re = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    if (!encontradas.includes(m[1])) encontradas.push(m[1]);
  }
  return encontradas;
}

/**
 * Limpa o HTML importado e devolve o relatório do que mudou.
 */
export function sanitizeStudioHtml(
  entrada: string | null | undefined,
  opcoes: StudioSanitizeOptions = {},
): StudioSanitizeResult {
  const permiteFormulario = opcoes.permitirFormulario === true;
  const tagsProibidas = permiteFormulario
    ? new Set([...STUDIO_FORBIDDEN_ELEMENTS].filter((t) => !STUDIO_FORM_TAGS.has(t)))
    : STUDIO_FORBIDDEN_ELEMENTS;
  const tagsPermitidas = permiteFormulario
    ? new Set([...STUDIO_ALLOWED_TAGS, ...STUDIO_FORM_TAGS])
    : STUDIO_ALLOWED_TAGS;

  const relatorio: StudioSanitizeReport = {
    removedElements: {},
    removedAttributes: {},
    slots: [],
    variables: [],
    externalImages: [],
    inlineImages: 0,
  };

  if (!entrada) return { html: '', report: relatorio };

  let html = String(entrada);

  // 1) Comentários (podem esconder condicionais executáveis)
  html = html.replace(/<!--[\s\S]*?-->/g, '');

  // 2) Elementos proibidos, com todo o conteúdo interno
  for (const tag of tagsProibidas) {
    const comConteudo = new RegExp(`<${tag}\\b[\\s\\S]*?<\\/${tag}\\s*>`, 'gi');
    html = html.replace(comConteudo, () => {
      conta(relatorio.removedElements, tag);
      return '';
    });
    const soAbertura = new RegExp(`<\\/?${tag}\\b[^>]*>`, 'gi');
    html = html.replace(soAbertura, () => {
      conta(relatorio.removedElements, tag);
      return '';
    });
  }

  // 3) Percorre as demais tags aplicando a lista permitida
  html = html.replace(
    /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g,
    (_todo, fechamento: string, nomeBruto: string, atributos: string) => {
      const nome = nomeBruto.toLowerCase();

      if (!tagsPermitidas.has(nome)) {
        conta(relatorio.removedElements, nome);
        return '';
      }

      if (fechamento) return `</${nome}>`;

      const autoFechada = /\/\s*$/.test(atributos);
      const limpos = saneiaAtributos(atributos.replace(/\/\s*$/, ''), relatorio, permiteFormulario);
      return `<${nome}${limpos}${autoFechada ? ' /' : ''}>`;
    },
  );

  relatorio.variables = detectStudioVariables(html);

  return { html: html.trim(), report: relatorio };
}

// =============================================================================
// Isolamento do conteúdo importado
// =============================================================================

/** Classe da raiz onde todo HTML importado é colocado. */
export const STUDIO_RAW_HTML_ROOT = 'studio-raw-html-root';

/** Relatório do isolamento aplicado ao conteúdo importado. */
export interface StudioScopeReport {
  /** Quantidade de regras `@import` removidas. */
  removedImports: number;
  /** Quantidade de regras globais (`html`, `body`, `*`) reescritas. */
  rewrittenGlobalRules: number;
  /** Quantidade de blocos `<style>` escopados. */
  scopedStyleBlocks: number;
}

/**
 * Escopa um seletor CSS para dentro da raiz do conteúdo importado.
 * Seletores globais viram a própria raiz, para nunca vazarem para o Studio.
 */
function escopaSeletor(seletor: string, relatorio: StudioScopeReport): string {
  return seletor
    .split(',')
    .map((parte) => {
      const limpo = parte.trim();
      if (!limpo) return '';
      // `@media`, `@supports` e afins são tratados fora daqui.
      if (limpo.startsWith('@')) return limpo;
      if (/^(html|body|:root|\*)$/i.test(limpo)) {
        relatorio.rewrittenGlobalRules += 1;
        return `.${STUDIO_RAW_HTML_ROOT}`;
      }
      // Remove referência a html/body no início de seletores compostos.
      const semGlobal = limpo.replace(/^(html|body)\s+/i, '');
      if (semGlobal !== limpo) relatorio.rewrittenGlobalRules += 1;
      return `.${STUDIO_RAW_HTML_ROOT} ${semGlobal}`;
    })
    .filter(Boolean)
    .join(', ');
}

/** Escopa todas as regras de uma folha de estilo. */
function escopaCss(css: string, relatorio: StudioScopeReport): string {
  // 1) `@import` nunca passa: traria CSS externo sem controle.
  let saida = css.replace(/@import[^;]+;/gi, () => {
    relatorio.removedImports += 1;
    return '';
  });

  // 2) `expression(...)` e URLs executáveis
  saida = saida
    .replace(/expression\s*\(/gi, '')
    .replace(/url\s*\(\s*["']?\s*(javascript|vbscript|data:text)/gi, 'url(');

  // 3) Cada bloco de regra recebe o prefixo da raiz.
  //    Regras dentro de `@media`/`@supports` são tratadas pelo mesmo caminho,
  //    porque a expressão da condição começa com `@` e é preservada.
  return saida.replace(/(^|})\s*([^{}@]+)\s*{/g, (todo, fecha: string, seletor: string) => {
    const escopado = escopaSeletor(seletor, relatorio);
    return escopado ? `${fecha}${escopado}{` : todo;
  });
}

/**
 * Escopa uma folha de estilo na raiz do conteúdo importado, sem exigir o
 * relatório. Usado pelo fatiamento em seções (`htmlSections.ts`), para não
 * existir uma segunda implementação de escopo no sistema.
 */
export function scopeStudioCss(css: string): string {
  return escopaCss(String(css ?? ''), {
    removedImports: 0,
    rewrittenGlobalRules: 0,
    scopedStyleBlocks: 0,
  });
}

/**
 * Coloca o HTML já saneado dentro de uma raiz isolada e escopa o CSS interno.
 * O resultado nunca afeta o restante da página (Studio ou página pública).
 */
export function scopeStudioHtml(htmlSaneado: string): {
  html: string;
  report: StudioScopeReport;
} {
  const relatorio: StudioScopeReport = {
    removedImports: 0,
    rewrittenGlobalRules: 0,
    scopedStyleBlocks: 0,
  };

  let html = String(htmlSaneado ?? '');

  // Já isolado (reimportação do mesmo conteúdo): nada a fazer.
  if (html.includes(`class="${STUDIO_RAW_HTML_ROOT}"`)) return { html, report: relatorio };

  // 1) Estilos internos: extraídos, escopados e reunidos em um único bloco.
  const estilos: string[] = [];
  html = html.replace(/<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi, (_todo, css: string) => {
    relatorio.scopedStyleBlocks += 1;
    estilos.push(escopaCss(css, relatorio));
    return '';
  });

  // 2) Estrutura de página completa vira apenas o conteúdo do corpo.
  const corpo = html.match(/<body\b[^>]*>([\s\S]*?)<\/body\s*>/i);
  if (corpo) html = corpo[1];
  html = html
    .replace(/<\/?(html|head|body|meta|title)\b[^>]*>/gi, '')
    .trim();

  const estilo = estilos.filter((c) => c.trim()).join('\n');

  return {
    html: `<div class="${STUDIO_RAW_HTML_ROOT}">${estilo ? `<style>${estilo}</style>` : ''}${html}</div>`,
    report: relatorio,
  };
}
